import * as CONSTANTS from "@/constants/constants";
import AuthService from "@/services/AuthService";
import {
    MIN_SEARCH_LENGTH,
    SEARCH_DEBOUNCE_MS,
    redirectUrlFor,
    isSameApp,
    filterOwnAccounts,
    headerUserName,
} from "@/services/masterAccess";

/**
 * "Trocar conta" do cabecalho: lista as contas do login, busca master e a
 * troca em si. E a mesma logica do Header.vue da linha dev/main; aqui num
 * mixin porque a linha beta nao renderiza o Header.vue desde o "global layout
 * refactor" (b0f6879f) — o cabecalho dela mora no App.vue, e era la que o
 * seletor faltava.
 *
 * Quem usa chama `load_switchable_accounts()` quando ha sessao: o App.vue
 * monta antes do login, e a lista so existe para um usuario logado.
 */
export const account_switcher_mixin = {
    data() {
        return {
            account_switcher_svc: new AuthService(),
            switchable_accounts: [],
            current_account_id: null,
            account_modal_open: false,
            switching_account: false,
            is_master: false,
            master_query: "",
            master_results: [],
            master_status: "idle",
            master_timer: null,
            master_request: 0,
            switch_error: null,
        };
    },
    computed: {
        can_switch_account() {
            // Master sempre pode trocar: a busca alcanca qualquer empresa ativa.
            return this.switchable_accounts.length > 1 || this.is_master;
        },
        header_user_name() {
            return headerUserName(this.user.user_name, this.switchable_accounts, this.current_account_id);
        },
        visible_master_results() {
            // A conta master em que o usuario esta agora ja aparece na lista de
            // cima como atual; o servidor nao sabe disso e a devolveria de novo.
            // So conta o que o filtro local do modal deixa visivel: uma conta
            // propria escondida pelo termo (busca por CNPJ ou codigo) ainda
            // precisa da sua linha no grupo master.
            const ids = filterOwnAccounts(this.switchable_accounts, this.master_query)
                .map((a) => a.id)
                .filter((id) => id !== null);
            return this.master_results.filter((a) => a.id === null || !ids.includes(a.id));
        },
    },
    methods: {
        open_account_modal() {
            // Reabrir o modal com um erro de uma tentativa anterior confunde: o
            // usuario ve "nao foi possivel" sem ter clicado em nada ainda.
            this.account_modal_open = true;
            this.switch_error = null;
        },
        close_account_modal() {
            // O modal volta vazio na proxima abertura (a busca dele e desmontada
            // junto); a busca master daqui acompanha, e o contador invalida uma
            // resposta que ainda esteja a caminho.
            this.account_modal_open = false;
            clearTimeout(this.master_timer);
            this.master_request++;
            this.master_query = "";
            this.master_results = [];
            this.master_status = "idle";
        },
        reset_switchable_accounts() {
            this.close_account_modal();
            this.switchable_accounts = [];
            this.current_account_id = null;
            this.is_master = false;
        },
        on_account_search(term) {
            this.master_query = term;
            if (this.is_master) {
                this.schedule_master_search();
            } else {
                this.switch_error = null;
            }
        },
        // Mesmo fluxo que src/services/v3/http.js aplica no 401 das suas
        // proprias chamadas. As chamadas daqui passam por Http.js (sem
        // interceptor), entao precisam replicar o tratamento na mao.
        handle_session_expired() {
            this.$store.dispatch("notification/add", {
                type: "error",
                message: "Sua sessão expirou.",
            });
            this.$store.dispatch("user/logout");
            window.location.assign(CONSTANTS.ROUTE_LOGIN);
        },
        load_switchable_accounts() {
            // Falha em silencio de proposito: o seletor e conveniencia, e um
            // erro aqui nao pode atrapalhar o cabecalho inteiro. Sem lista, sem
            // seletor.
            this.account_switcher_svc
                .my_accounts()
                .then(({ data }) => {
                    this.switchable_accounts = (data && data.accounts) || [];
                    this.current_account_id = data && data.current_user_id;
                    this.is_master = Boolean(data && data.is_master);
                })
                .catch((err) => {
                    if (err && err.response && err.response.status === 401) {
                        this.handle_session_expired();
                        return;
                    }
                    this.switchable_accounts = [];
                    this.is_master = false;
                });
        },
        schedule_master_search() {
            clearTimeout(this.master_timer);
            // Modal reaberto com o erro da troca anterior, ou nova busca
            // digitada por cima dele: nenhum dos dois ainda vale para o que o
            // usuario esta fazendo agora.
            this.switch_error = null;
            const term = this.master_query.trim();
            // Contador, nao cancelamento: a resposta de um termo antigo que
            // chegue depois da nova nao pode sobrescrever a lista.
            const request = ++this.master_request;

            if (term.length < MIN_SEARCH_LENGTH) {
                this.master_results = [];
                this.master_status = "idle";
                return;
            }

            this.master_status = "loading";
            this.master_timer = setTimeout(() => {
                this.account_switcher_svc
                    .search_master_accounts(term)
                    .then(({ data }) => {
                        if (request !== this.master_request) return;
                        this.master_results = data || [];
                        this.master_status = "idle";
                    })
                    .catch((err) => {
                        if (err && err.response && err.response.status === 401) {
                            this.handle_session_expired();
                            return;
                        }
                        if (request !== this.master_request) return;
                        this.master_results = [];
                        this.master_status = "error";
                    });
            }, SEARCH_DEBOUNCE_MS);
        },
        switch_account(account) {
            if (this.switching_account || (account.id !== null && account.id === this.current_account_id)) {
                this.account_modal_open = false;
                return;
            }

            this.switching_account = true;
            this.switch_error = null;

            this.account_switcher_svc
                .switch_account(account)
                .then(({ data }) => {
                    const target = redirectUrlFor(account.type, data.token, {
                        providerUrl: CONSTANTS.PROVIDER_URL,
                        adminUrl: CONSTANTS.ADMIN_URL,
                    });

                    // Sem destino configurado, nada de logout: a sessao atual
                    // continua valida e o usuario fica onde esta.
                    if (!target) {
                        this.switching_account = false;
                        this.switch_error = "URL de redirecionamento não configurada para esta conta.";
                        return;
                    }

                    if (isSameApp(account.type)) {
                        // Trocar de conta NAO e so trocar o token: o store
                        // guarda perms, lojas, settings, cli_id e companhia da
                        // conta anterior em localStorage. Reaproveitar o
                        // `logout` garante que essa limpeza acompanhe o que o
                        // store de fato guarda.
                        this.$store.dispatch("user/logout");

                        // Cliente volta pelo mesmo caminho do login (routes.js
                        // grava o ?token=).
                        window.location.href = target;
                        return;
                    }

                    // Saida para outro app (fornecedor ou admin) nao pode
                    // derrubar a sessao deste painel no meio da navegacao: as
                    // paginas reativas refazem suas chamadas antes do browser
                    // sair, tomam 401 sem token e redirecionam para /login,
                    // cancelando a entrega do target. O app de destino adota o
                    // token novo sozinho.
                    window.location.assign(target);
                })
                .catch(() => {
                    // Recusa (empresa desativada, master revogado) nao derruba a
                    // sessao: mostra o erro e recarrega a lista, que pode ter
                    // mudado.
                    this.switching_account = false;
                    this.switch_error = "Não foi possível entrar nesta conta agora.";
                    this.load_switchable_accounts();
                });
        },
    },
    beforeDestroy() {
        clearTimeout(this.master_timer);
    },
};
